import { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SelectField } from '../../components/ui/SelectField';
import { ApiError } from '../../lib/api';
import { declaredAssetType } from '../../lib/portalAssets/allowlist';
import { ADAPT_FOR_OPTIONS } from '../../lib/portalInspiration/adaptFor';
import { INSPIRATION_USAGES, INSPIRATION_USAGE_LABELS, type InspirationAdaptFor, type InspirationUsage } from '../../types/portalInspiration';
import type { PortalAudienceRule } from '../../types/portalLibrary';
import type { Account } from '../../types/account';
import type { PortalAssetsApi } from '../portalAssets/assetsApi';
import { TagChipInput } from '../portalAssets/TagChipInput';
import { uploadAssetFiles, uploadSummary, type UploadRow } from '../portalAssets/uploadFiles';
import { AudienceRulePicker } from '../portalLibrary/AudienceEditor';
import { completeInspirationUpload, createInspirationUploads, getKyckConsent } from './api';
import { draftEntry } from './draftClient';

// Tools → Inspiration — the UPLOAD PANEL (single and bulk).
//
// ⚠️ It reuses the portal-assets upload RUN (uploadAssetFiles: byte checks, in-batch duplicate skip, ≤50
// per request, ≤4 at once, resumable PUTs, `complete`) through a small ADAPTER, so there is one upload
// implementation. The adapter is the only thing that knows the inspiration endpoints; the reserved
// account is chosen on the SERVER, so this panel never names an account.
//
// ⚠️ Batch defaults (audience, usage, tags) apply to every file; nothing is asked about permission
// (Scott). Entries land as DRAFTS — clients see nothing until each is published.
//
// ⚠️ "Draft text with AI after upload" is an EXPLICIT staff choice made here, before upload, so it is
// on-demand: it never runs unless this box is ticked, and it reads the File still in memory.

const ADAPT_OPTIONS = [{ value: '', label: 'From the audience (when it is one industry)' }, ...ADAPT_FOR_OPTIONS.map((o) => ({ value: o.value as string, label: o.label }))];
const USAGE_OPTIONS = [{ value: '', label: 'Decide later' }, ...INSPIRATION_USAGES.map((u) => ({ value: u, label: INSPIRATION_USAGE_LABELS[u] }))];

type Props = {
  accounts: Account[];
  /** Hands the just-uploaded Files to the page (entry id → File) so a later Draft can read them from memory. */
  onUploaded: (files: Map<string, File>) => void;
  onChanged: () => void;
};

export function InspirationUploadPanel({ accounts, onUploaded, onChanged }: Props) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [audience, setAudience] = useState<PortalAudienceRule[]>([]);
  const [usage, setUsage] = useState<InspirationUsage | ''>('');
  const [tags, setTags] = useState<string[]>([]);
  const [adaptFor, setAdaptFor] = useState<InspirationAdaptFor | ''>('');
  const [draftAfter, setDraftAfter] = useState(false);
  const [note, setNote] = useState('');
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [running, setRunning] = useState(false);
  const [drafting, setDrafting] = useState<string | null>(null);

  async function start() {
    if (files.length === 0 || running) return;
    setRunning(true);
    setRows([]);
    try {
      const consent = await getKyckConsent();
      const entryByAsset = new Map<string, string>();
      const entryByName = new Map<string, string>();
      const adapter: Pick<PortalAssetsApi, 'createUploads' | 'complete'> = {
        createUploads: async (req) => {
          const r = await createInspirationUploads({
            consentWordingVersion: consent.version,
            files: req.files,
            defaults: { audience, usage: usage === '' ? null : usage, tags, ...(adaptFor === '' ? {} : { adaptFor }) },
          });
          for (const i of r.items) {
            if (i.entryId) {
              entryByAsset.set(i.assetId, i.entryId);
              entryByName.set(i.filename, i.entryId);
            }
          }
          if (r.entryFailures.length > 0) {
            toast.error(`${r.entryFailures.length} file${r.entryFailures.length === 1 ? '' : 's'} uploaded without an entry — upload ${r.entryFailures.length === 1 ? 'it' : 'them'} again.`);
          }
          return {
            uploads: r.items.map((i) => ({ assetId: i.assetId, uploadUrl: i.uploadUrl, requiredHeaders: i.requiredHeaders })),
            skipped: r.skipped,
            duplicateCheckRan: r.duplicateCheckRan,
          };
        },
        complete: async (assetId) => {
          const entryId = entryByAsset.get(assetId);
          if (!entryId) throw new Error('Unknown upload.');
          return completeInspirationUpload(entryId) as never;
        },
      };
      const result = await uploadAssetFiles({
        api: adapter,
        accountId: '',
        consentWordingVersion: consent.version,
        files,
        onChange: setRows,
        onNotice: (s) => toast.message(s),
      });

      // Hand the Files to the page, keyed by entry, so Draft can read them without a download.
      const mine = new Map<string, File>();
      for (const row of result) {
        if (row.status !== 'done') continue;
        const file = files[row.key]!;
        const entryId = entryByName.get(file.name);
        if (entryId) mine.set(entryId, file);
      }
      onUploaded(mine);
      onChanged();

      if (draftAfter && mine.size > 0) await draftAll(mine, files.length === 1 ? note : '');
    } catch (err) {
      toast.error(err instanceof ApiError || err instanceof Error ? err.message : 'The upload failed.');
    } finally {
      setRunning(false);
      setDrafting(null);
    }
  }

  async function draftAll(mine: Map<string, File>, theNote: string) {
    let ok = 0;
    const problems: string[] = [];
    let i = 0;
    for (const [entryId, file] of mine) {
      i++;
      setDrafting(`Drafting ${i} of ${mine.size}…`);
      const mediaType = declaredAssetType(file.name, file.type)?.mediaType ?? 'image';
      try {
        await draftEntry({ entryId, mediaType, asset: null, file, note: theNote });
        ok++;
      } catch (err) {
        problems.push(`${file.name}: ${err instanceof Error ? err.message : 'failed'}`);
      }
    }
    onChanged();
    if (problems.length === 0) toast.success(`Drafted ${ok} with AI. Review each before publishing.`);
    else toast.error(`Drafted ${ok}; ${problems.length} could not be drafted (${problems[0]}). Use Draft with AI on those from the list.`);
  }

  const summary = uploadSummary(rows);

  return (
    <div className="space-y-4 rounded-lg border border-slate-200 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={fileInput}
          type="file"
          multiple
          accept="image/*,video/*,.heic,.heif"
          className="hidden"
          onChange={(e) => {
            setFiles([...(e.target.files ?? [])]);
            setRows([]);
          }}
        />
        <Button type="button" variant="outline" onClick={() => fileInput.current?.click()} disabled={running}>
          <Upload className="mr-1 h-4 w-4" /> Choose photos and videos
        </Button>
        <span className="text-sm text-slate-600">
          {files.length === 0 ? 'Up to 50 files at a time.' : `${files.length} file${files.length === 1 ? '' : 's'} chosen`}
        </span>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Who can see them (applies to every file)</Label>
          <AudienceRulePicker
            value={audience}
            onChange={setAudience}
            accounts={accounts}
            emptyHint="Choose who can see it. You can change this per entry, and nothing goes live until you publish."
          />
        </div>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Use for</Label>
            <SelectField value={usage} onValueChange={(v) => setUsage(v as InspirationUsage | '')} options={USAGE_OPTIONS} aria-label="Use for" />
          </div>
          <div className="space-y-1.5">
            <Label>Adapt for</Label>
            <SelectField value={adaptFor} onValueChange={(v) => setAdaptFor(v as InspirationAdaptFor | '')} options={ADAPT_OPTIONS} aria-label="Adapt for" />
            <p className="text-xs text-slate-500">For trends unrelated to the client's business: the AI adapts the idea for this kind of business.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Tags</Label>
            <TagChipInput value={tags} onChange={setTags} />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm text-slate-800">
          <Checkbox checked={draftAfter} onCheckedChange={(v) => setDraftAfter(v === true)} aria-label="Draft text with AI after upload" />
          Draft text with AI after upload
        </label>
        {draftAfter && files.length === 1 && files[0] && declaredAssetType(files[0].name, files[0].type)?.mediaType === 'video' && (
          <div className="space-y-1">
            <Label htmlFor="insp-up-note">What happens / the trendLine (optional — the AI can’t hear audio)</Label>
            <Input id="insp-up-note" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        )}
        {draftAfter && <p className="text-xs text-slate-500">Each video is read at about two frames a second, so a draft can take up to a minute. You review every draft before anything is published.</p>}
      </div>

      <div className="flex items-center gap-3">
        <Button type="button" onClick={() => void start()} disabled={files.length === 0 || running}>
          {running ? 'Uploading…' : `Upload ${files.length || ''} as drafts`.trim()}
        </Button>
        {drafting && <span className="text-sm text-slate-600">{drafting}</span>}
      </div>

      {rows.length > 0 && (
        <div className="space-y-1">
          <ul className="max-h-48 space-y-0.5 overflow-y-auto text-sm">
            {rows.map((r) => (
              <li key={r.key} className="flex justify-between gap-3">
                <span className="truncate">{r.filename}</span>
                <span className={r.status === 'failed' || r.status === 'refused' ? 'text-destructive' : 'text-slate-600'}>
                  {r.status === 'uploading' ? `${Math.round(r.progress * 100)}%` : r.status === 'done' ? 'Uploaded' : r.reason ?? r.status}
                </span>
              </li>
            ))}
          </ul>
          {summary && <p className="text-xs text-slate-600">{summary.replace('Uploaded files appear in your library once processed.', 'They appear below as drafts once processed.')}</p>}
        </div>
      )}
    </div>
  );
}
